import { Chip } from '@heroui/react';
import React from 'react';
import { FiDownload } from 'react-icons/fi';

import { AlertCard } from '../../components/AlertCard';
import { ErrorCard } from '../../components/ErrorCard';
import { PageHeader } from '../../components/PageHeader';

import {
  CsvDropzone,
  IssuesTable,
  UploadInstructions,
  UploadPreview,
  UploadResults,
} from './components';
import { useOrderGuideUpload } from './hooks/useOrderGuideUpload';
import { downloadTemplateCsv } from './utils/csvTemplate';

export const OrderGuideUploadPage: React.FC = () => {
  const {
    phase,
    fileName,
    fileError,
    lookupError,
    issues,
    groups,
    results,
    didWrite,
    mode,
    setMode,
    processFile,
    upload,
    reset,
  } = useOrderGuideUpload();

  const hasBlockingIssues = issues.some((issue) => issue.severity === 'error');
  const showIntake = phase === 'idle' || phase === 'processing';
  const showReview = phase === 'review';
  const showResults = phase === 'uploading' || phase === 'done';

  return (
    <div className="space-y-6">
      <PageHeader
        actions={[
          {
            label: 'Download template',
            icon: <FiDownload />,
            onClick: downloadTemplateCsv,
            variant: 'flat',
          },
        ]}
        title="Order Guide Upload"
        description="Bulk upload order guide products from a CSV file — supports chats with multiple order guides."
      />

      {showIntake && (
        <>
          {fileError && (
            <AlertCard
              type="error"
              title={
                fileName ? `Could not read ${fileName}` : 'Could not read file'
              }
              message={fileError}
            />
          )}
          <CsvDropzone
            onFile={processFile}
            isProcessing={phase === 'processing'}
          />
          <UploadInstructions />
        </>
      )}

      {showReview && (
        <>
          {fileName && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-default-500">File:</span>
              <Chip variant="flat" size="sm">
                {fileName}
              </Chip>
            </div>
          )}

          {lookupError ? (
            <ErrorCard
              title="Chat lookup failed"
              message={`The chats in this file could not be checked: ${lookupError}`}
              action={{
                label: 'Start over',
                onPress: reset,
                variant: 'flat',
              }}
            />
          ) : (
            <>
              {issues.length > 0 && <IssuesTable issues={issues} />}
              {hasBlockingIssues ? (
                <AlertCard
                  type="error"
                  title="Upload blocked"
                  message="Fix the errors above in the CSV file and upload it again. Nothing has been sent to the backend."
                />
              ) : (
                <UploadPreview
                  groups={groups}
                  mode={mode}
                  onModeChange={setMode}
                  onUpload={upload}
                  onReset={reset}
                  canUpload={groups.length > 0}
                />
              )}
              {hasBlockingIssues && (
                <CsvDropzone onFile={processFile} isProcessing={false} />
              )}
            </>
          )}
        </>
      )}

      {showResults && (
        <>
          {phase === 'done' && !didWrite && (
            <AlertCard
              type="warning"
              title="Nothing was uploaded"
              message="Backend validation failed for at least one order guide, so no data was written. Fix the rejected products and try again."
            />
          )}
          <UploadResults
            results={results}
            isUploading={phase === 'uploading'}
            onReset={reset}
          />
        </>
      )}
    </div>
  );
};
